import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { ArrowBack, Edit, Lock, Receipt, Close } from '@mui/icons-material';
import { format } from 'date-fns';
import expensesService from '../../services/expensesService';

const ExpenseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [expense, setExpense] = useState(null);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchExpense = useCallback(async () => {
    try {
      setLoading(true);
      const data = await expensesService.getExpense(id);
      setExpense(data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You do not have permission to view this expense.');
      } else {
        setError('Failed to fetch expense details');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchExpense();
  }, [fetchExpense]);

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      await expensesService.approveExpense(id);
      fetchExpense();
    } catch (err) {
      setError('Failed to approve expense');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setActionLoading(true);
      await expensesService.rejectExpense(id, rejectReason);
      setRejectDialogOpen(false);
      fetchExpense();
    } catch (err) {
      setError('Failed to reject expense');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!expense) return <Box sx={{ p: 3 }}><Alert severity="info">Expense not found</Alert></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3, 
        flexWrap: 'wrap', 
        gap: 2 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">
            Expense {expense.reference_code}
          </Typography>
          {expense.is_locked && (
            <Chip 
              icon={<Lock />} 
              label="Locked Period" 
              color="default" 
              variant="outlined" 
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/expenses')}
          >
            Back
          </Button>
          {expense.status === 'draft' && !expense.is_locked && (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => navigate(`/admin/expenses/${id}/edit`)}
              disabled={actionLoading}
            >
              Edit
            </Button>
          )}
          {expense.status === 'draft' && (
            <>
              <Button 
                variant="contained" 
                color="success" 
                onClick={handleApprove}
                disabled={actionLoading}
              >
                Approve
              </Button>
              <Button 
                variant="contained" 
                color="error" 
                onClick={() => setRejectDialogOpen(true)}
                disabled={actionLoading}
              >
                Reject
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab label="Details" />
            <Tab label="Audit Logs" />
          </Tabs>

          {tabValue === 0 && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                <Typography variant="h6">{expense.title}</Typography>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <Typography variant="body1">{expense.description || '-'}</Typography>
                </Box>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                    <Typography variant="body1">{expense.category}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">Expense Date</Typography>
                    <Typography variant="body1">
                      {expense.expense_date ? format(new Date(expense.expense_date), 'MMM dd, yyyy') : '-'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    <Chip 
                      label={expense.status} 
                      color={expense.status === 'approved' ? 'success' : expense.status === 'rejected' ? 'error' : 'warning'} 
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Financials</Typography>
                    <Divider sx={{ mb: 1 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Original Amount</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {expense.currency} {parseFloat(expense.original_amount).toLocaleString()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Amount (NGN)</Typography>
                      <Typography variant="body1">
                        ₦{parseFloat(expense.amount_in_ngn).toLocaleString()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Amount (CNY)</Typography>
                      <Typography variant="body1">
                        ¥{parseFloat(expense.amount_in_cny).toLocaleString()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Exchange Rate</Typography>
                      <Typography variant="body2">
                        {expense.exchange_rate_used}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Created By</Typography>
                  <Typography variant="body2">{expense.created_by_name || 'System'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(expense.created_at), 'MMM dd, yyyy HH:mm')}
                  </Typography>
                </Box>

                {expense.approved_by && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Approved By</Typography>
                    <Typography variant="body2">{expense.approved_by_name}</Typography>
                  </Box>
                )}
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">Receipt</Typography>
                  {expense.receipt ? (
                      <Button 
                        onClick={() => setPreviewOpen(true)}
                        variant="outlined" 
                        startIcon={<Receipt />}
                        sx={{ mt: 1 }}
                      >
                          View Receipt
                      </Button>
                  ) : (
                      <Typography variant="body2">No receipt attached</Typography>
                  )}
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Box>
                {expense.audit_logs && expense.audit_logs.length > 0 ? (
                    expense.audit_logs.map((log) => (
                        <Box key={log.id} sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                            <Typography variant="subtitle2">
                                {log.action} by {log.performed_by_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm:ss')}
                            </Typography>
                            {/* Assuming audit logs return old_data/new_data as JSON */}
                            <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto', bgcolor: '#f5f5f5', p: 1 }}>
                                <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                                    {JSON.stringify(log.new_data || log.old_data, null, 2)}
                                </pre>
                            </Box>
                        </Box>
                    ))
                ) : (
                    <Typography>No audit logs available</Typography>
                )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>Reject Expense</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for rejection"
            fullWidth
            multiline
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReject} color="error" variant="contained">Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Receipt Preview Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Receipt Preview
          <IconButton
            aria-label="close"
            onClick={() => setPreviewOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {expense?.receipt && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              {expense.receipt.toLowerCase().endsWith('.pdf') ? (
                 <iframe src={expense.receipt} title="Receipt PDF" width="100%" height="500px" style={{ border: 'none' }} />
              ) : (
                <img 
                  src={expense.receipt} 
                  alt="Receipt" 
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} 
                  onError={(e) => {
                    e.target.onerror = null; 
                    e.target.src = ''; 
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          <Button href={expense?.receipt} target="_blank" rel="noopener noreferrer" variant="contained">
            Open in New Tab
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseDetail;
